// @flow
import React, { useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Typography from "../Typography";
import BasicButton from "../BasicButton";
import ConditionalWrapper from "../ConditionalWrapper";
import useModalHook from "../../hooks/useModalHook";
import useMatchMediaMatches from "../../hooks/useMatchMediaMatches";
import useOutsideClickHandler from "../../hooks/useOutsideClickHandler";
import Radio from "../Radio";
import SearchFormFullscreenHeader from "../SearchFormFullscreenHeader";

import sharedStyles from "../SearchFormDateInput/searchFormDateInput.module.scss";
import styles from "./searchFormMonthlyDurationInput.module.scss";

type Props = {
  selected: string,
  onChange: (string) => void
};

const optionData = {
  WEEKDAYS_ONLY: {
    value: "WEEKDAYS_ONLY",
    name: "Mon-Fri only",
    description: "Just reserve the space for week days."
  },
  STANDARD: {
    value: "STANDARD",
    name: "7 days per week",
    description: "Guarantee the space for any day of the week."
  }
};

const optionDataValues = Object.values(optionData);

const cypress = "search-form-monthly-duration-input";

const SearchFormMonthlyDurationInput = ({ selected, onChange }: Props) => {
  const ref = useRef();
  const [pickerOpen, setPickerOpen] = useState(false);
  const isMobileLayout = useMatchMediaMatches("(max-width: 639px)");

  const showInWrapper = pickerOpen && isMobileLayout;
  useModalHook(showInWrapper, "modal-open");

  const onOutsideClick = useCallback(() => {
    setPickerOpen(false);
  }, [setPickerOpen]);
  useOutsideClickHandler(ref, onOutsideClick, pickerOpen);

  const onCancel = useCallback(() => {
    setPickerOpen(false);
  }, [setPickerOpen]);

  const onSelectItem = useCallback(
    (value) => {
      setPickerOpen(false);
      onChange(value);
    },
    [onChange]
  );

  return (
    <div className={`${sharedStyles.alignRight} ${sharedStyles.base}`}>
      <BasicButton
        className={sharedStyles.triggerButton}
        onClick={() => {
          setPickerOpen(true);
        }}
        cypress={cypress}
      >
        <Typography color="brand" className={sharedStyles.label}>
          Monthly Duration
        </Typography>

        <Typography className={sharedStyles.dateText}>
          {optionData[selected].name}
        </Typography>
      </BasicButton>

      {pickerOpen && (
        <ConditionalWrapper
          condition={showInWrapper}
          wrapper={(children) => {
            return createPortal(children, document.getElementById("portal"));
          }}
        >
          <div
            ref={ref}
            className={
              showInWrapper ? styles.fullscreenModal : styles.pickerContainer
            }
            data-cy={`${cypress}-dropdown`}
          >
            {showInWrapper && (
              <SearchFormFullscreenHeader
                header="Monthly Duration"
                onCancel={onCancel}
              />
            )}

            {optionDataValues.map(({ value, name, description }) => {
              const isSelectedOption = selected === value;
              return (
                <BasicButton key={value} onClick={() => onSelectItem(value)}>
                  <div
                    className={styles.optionItem}
                    data-selected={isSelectedOption}
                  >
                    <div className={styles.optionTextContainer}>
                      <Typography>
                        <div className={styles.optionLabel}>{name}</div>
                      </Typography>
                      <Typography>
                        <div className={styles.optionDescription}>
                          {description}
                        </div>
                      </Typography>
                    </div>

                    <Radio
                      name="monthlyDuration"
                      checked={isSelectedOption}
                      onChange={() => {}}
                      value={value}
                      className={styles.optionRadio}
                    />
                  </div>
                </BasicButton>
              );
            })}
          </div>
        </ConditionalWrapper>
      )}
    </div>
  );
};

export default SearchFormMonthlyDurationInput;
